/**
 * Idle and AFK on the wire (ADR-090): a character's away state, which the map server decides and the snapshot's
 * status byte carries beside the Infamy tier (PROTOCOL.md 5.2), so a transition costs one byte in the next delta
 * and nothing a tick.
 */

/**
 * A character's away state: active, idle (no player-driven input for a while, or its page away), AFK (longer, or
 * `/afk`), or held: a Game Master has frozen it (ADR-056's amendment of 2026-10-06, decision 45), which the map
 * server writes whatever its idle tracker holds, and which the own-page `away` message never carries.
 */
export const Away = { Active: 0, Idle: 1, Afk: 2, Held: 3 } as const;

/**
 * One of `Away`'s values.
 */
export type AwayState = (typeof Away)[keyof typeof Away];

/**
 * The largest away state the status byte's two bits carry: `Away.Held`, for which a page from before the value had a
 * meaning draws no mark.
 */
export const MAX_AWAY = 3;

/**
 * The largest Infamy tier the status byte carries: bits 0 and 1 hold the tier, and bits 2 to 5 hold the badge
 * (ADR-112 decision 5.3), so a tier above 3 would write into the badge. The encoder's bound and the clamp the map
 * server spawns a character with.
 */
export const MAX_STATUS_INFAMY = 3;

/**
 * The largest badge the status byte carries: bits 2 to 5.
 */
export const MAX_STATUS_BADGE = 15;

/**
 * Where the badge sits in the status byte.
 */
const BADGE_SHIFT = 2;

/**
 * Where the away state sits in the status byte.
 */
const AWAY_SHIFT = 6;

/**
 * The status byte for an Infamy tier, an away state and a badge.
 *
 * @param infamy The tier, 0 to `MAX_STATUS_INFAMY`.
 * @param away The away state, 0 to `MAX_AWAY`.
 * @param badge The badge, 0 to `MAX_STATUS_BADGE`; 0 writes the byte a build without badges writes.
 * @returns The byte.
 */
export function statusByte(infamy: number, away: number, badge = 0): number {
  return (infamy & MAX_STATUS_INFAMY) | ((badge & MAX_STATUS_BADGE) << BADGE_SHIFT) | ((away & MAX_AWAY) << AWAY_SHIFT);
}

/**
 * The Infamy tier a status byte carries.
 *
 * @param byte The status byte.
 * @returns The tier, 0 to 3, from bits 0 and 1 alone.
 */
export function infamyOfStatus(byte: number): number {
  return byte & MAX_STATUS_INFAMY;
}

/**
 * The badge a status byte carries.
 *
 * @param byte The status byte.
 * @returns The badge, 0 to 15, from bits 2 to 5.
 */
export function badgeOfStatus(byte: number): number {
  return (byte >> BADGE_SHIFT) & MAX_STATUS_BADGE;
}

/**
 * The away state a status byte carries.
 *
 * @param byte The status byte.
 * @returns The away state, 0 to 3.
 */
export function awayOfStatus(byte: number): number {
  return (byte >> AWAY_SHIFT) & MAX_AWAY;
}

/**
 * Whether a status byte says its character is held: a Game Master has frozen it.
 *
 * @param byte The status byte.
 * @returns True when the away state it carries is `Away.Held`.
 * @example
 * heldOfStatus(statusByte(0, Away.Held));
 * // true
 */
export function heldOfStatus(byte: number): boolean {
  return awayOfStatus(byte) === Away.Held;
}
